<template>

<div class="main list-view no-sub-header">

	<div class="filters">

		<div class="label-group">
			<label>Start</label>
			<input type="date" v-model="filters.start_date" :max="$today().clone().format('YYYY-MM-DD')">
		</div>
		<div class="label-group">
			<label>End</label>
			<input type="date" v-model="filters.end_date" :max="$today().clone().format('YYYY-MM-DD')">
		</div>
		<div class="label-group">
			<label></label>
			<div v-if="filteredList" class="button small neutral" @click="getFilteredList()">Filter Sales Dates</div>
			<div v-else class="button small neutral" @click="getList()">Filter Sales Dates</div>
		</div>

		<div class="note" style="margin: 1em 0;">This report compares sales to <b><i>current</i></b> inventory. Dates apply to sales period only. 
			<span v-if="getClient.client_type_code == 'CDS'">Use the <router-link to="/reports/inventory-snapshot"><b>Inventory Snapshot Report</b></router-link> for past inventory levels.</span>
		</div>
		<div class="divider"></div>

		<select v-if="filters.department_id && categories.length > 0" v-model="filters.category_id" @change="getFilteredList()">
			<option value="">Category...</option>
			<option v-for="(item, index) in categories" :value="item.category_id">{{ item.description }}</option>
		</select>

		<input type="search" v-model="filters.search_term" placeholder="UPC Search..." @search="getFilteredList()">

		<select v-model="filters.manufacturer_id" @change="getFilteredList()">
			<option value="">Manufacturer...</option>
			<option v-for="(item, index) in manufacturers" :value="item.manufacturer_id">{{ item.description }}</option>
		</select>

		<div class="divider"></div>
		<div class="button neutral" @click="clearFilters()">Clear Filters</div>
		<div v-if="filteredList" class="button neutral" @click="backToDepartments()">Back To Departments</div>

		<div class="divider"></div>
		<div :class="['extra-menu-item', gettingExport ? 'disabled' : '']" @click="doExport()">Export CSV</div>
		<div class="extra-menu-item" @click="printWindow()">Print Report</div>
        <div v-if="getDevEnvironment" class="extra-menu-item" @click="getList()">Reload Report</div>

		<div v-if="legacyLink" class="divider"></div>
		<router-link v-if="legacyLink" :to="legacyLink" class="extra-menu-item">Legacy Version</router-link>

	</div>

	<div style="margin: 0 1em;">

		<div v-if="loading" class="loading"></div>

		<div v-else-if="filteredList" id="list_wrapper" class="list report-list no-sub-header">
			<div class="printable-heading">Inventory Management Report, {{ filteredList }}: {{ filters.start_date }} through {{ filters.end_date }}</div>
			<h3>{{ filteredList }}</h3>
			<table ref="list" id="list_table" class="data-list" cellpadding="0" cellspacing="0">
				<thead>
					<tr>
						<th class="list">Product</th>
						<th class="list right">Sales</th>
						<th class="list right">Cost Of<br>Goods<br>Sold</th>
						<th class="list right">Gross<br>Sales<br>Margin</th>
						<th class="list right">Gross<br>Profit</th>
						<th class="list right">Items<br>{{ clientTypeCode == 'FFL_OFFICE' ? 'Moved' : 'Sold' }}</th>
						<th class="list right">Items<br>On Hand</th>
						<th class="list right">On Hand<br>Cost</th>
						<th class="list right">On Hand<br>Retail</th>
						<th class="list right">Gross<br>On Hand<br>Margin</th>
						<th class="list right">Turn</th>
						<th class="list right">GMROI</th>
					</tr>
				</thead>
				<tr v-for="(item, index) in productList" class="clickable-list-item" @click="goProduct(item)">
					<td class="list left" style="max-width: 160px;">{{ item.description }}<br><label v-if="item.upc">UPC</label> {{ item.upc }}</td>
					<td class="list right">{{ $formatCurrency(item.price_sold) }}</td>
					<td class="list right">{{ $formatCurrency(item.cost_sold) }}</td>
					<td class="list right">{{ item.profit_percent }}%</td>
					<td class="list right">{{ $formatCurrency(item.profit_sold) }}</td>
					<td class="list right">{{ item.quantity_sold || '0' }}</td>
					<td class="list right">{{ item.quantity || '0' }}</td>
					<td class="list right">{{ $formatCurrency(item.cost) }}</td>
					<td class="list right">{{ $formatCurrency(item.price) }}</td>
					<td class="list right">{{ item.on_hand_margin }}%</td>
					<td class="list right">{{ item.turnover }}</td>
					<td class="list right">{{ item.gmroi }}</td>
				</tr>
				<tr class="list_row_totals">
					<td class="list left">TOTALS</td>
					<td class="list right">{{ $formatCurrency(getColumnTotal('price_sold')) }}</td>
					<td class="list right">{{ $formatCurrency(getColumnTotal('cost_sold')) }}</td>
					<td class="list right">{{ getMargin(getColumnTotal('cost_sold'), getColumnTotal('price_sold')) }}%</td>
					<td class="list right">{{ $formatCurrency(getColumnTotal('profit_sold')) }}</td>
					<td class="list right">{{ getColumnTotal('quantity_sold') }}</td>
					<td class="list right">{{ getColumnTotal('quantity') }}</td>
					<td class="list right">{{ $formatCurrency(getColumnTotal('cost')) }}</td>
					<td class="list right">{{ $formatCurrency(getColumnTotal('price')) }}</td>
					<td class="list right">{{ getMargin(getColumnTotal('cost'), getColumnTotal('price')) }}%</td>
					<td class="list right"></td>
					<td class="list right"></td>
				</tr>
			</table>
		</div>

		<div v-else id="list_wrapper" class="list report-list no-sub-header">
			<div class="printable-heading">Inventory Management Report: {{ filters.start_date }} through {{ filters.end_date }}</div>
			<table ref="list" id="list_table" class="data-list" cellpadding="0" cellspacing="0">
				<thead>
					<tr>
						<th class="list">Department</th>
						<th class="list right">Sales</th>
						<th class="list right">Cost Of<br>Goods<br>Sold</th>
						<th class="list right">Gross<br>Sales<br>Margin</th>
						<th class="list right">Gross<br>Profit</th>
						<th class="list right">Items<br>{{ clientTypeCode == 'FFL_OFFICE' ? 'Moved' : 'Sold' }}</th>
						<th class="list right">Items<br>On Hand</th>
						<th class="list right">On Hand<br>Cost</th>
						<th class="list right">On Hand<br>Retail</th>
						<th class="list right">Gross<br>On Hand<br>Margin</th>
						<th class="list right">Turn</th>
						<th class="list right">GMROI</th>
					</tr>
				</thead>
				<tr v-for="(item, index) in list" class="clickable-list-item" @click="getDepartment(item.department_id)">
					<td class="list left" style="max-width: 120px;">{{ item.description }}</td>
					<td class="list right">{{ $formatCurrency(item.price_sold) }}</td>
					<td class="list right">{{ $formatCurrency(item.cost_sold) }}</td>
					<td class="list right">{{ item.profit_percent }}%</td>
					<td class="list right">{{ $formatCurrency(item.profit_sold) }}</td>
					<td class="list right">{{ item.quantity_sold || '0' }}</td>
					<td class="list right">{{ item.quantity || '0' }}</td>
					<td class="list right">{{ $formatCurrency(item.cost) }}</td>
					<td class="list right">{{ $formatCurrency(item.price) }}</td>
					<td class="list right">{{ item.on_hand_margin }}%</td>
					<td class="list right">{{ item.turnover }}</td>
					<td class="list right">{{ item.gmroi }}</td>
				</tr>
				<tr class="list_row_totals">
					<td class="list left">TOTALS</td>
					<td class="list right">{{ $formatCurrency(getColumnTotal('price_sold')) }}</td>
					<td class="list right">{{ $formatCurrency(getColumnTotal('cost_sold')) }}</td>
					<td class="list right">{{ getMargin(getColumnTotal('cost_sold'), getColumnTotal('price_sold')) }}%</td>
					<td class="list right">{{ $formatCurrency(getColumnTotal('profit_sold')) }}</td>
					<td class="list right">{{ getColumnTotal('quantity_sold') }}</td>
					<td class="list right">{{ getColumnTotal('quantity') }}</td>
					<td class="list right">{{ $formatCurrency(getColumnTotal('cost')) }}</td>
					<td class="list right">{{ $formatCurrency(getColumnTotal('price')) }}</td>
					<td class="list right">{{ getMargin(getColumnTotal('cost'), getColumnTotal('price')) }}%</td>
					<td class="list right"></td>
					<td class="list right"></td>
				</tr>

			</table>
			<!-- <div v-if="overrides.override_total > 0" class="three-columns" style="margin: 1em 5em; width: 30em;">
				<div><b>Override Products</b></div>
				<div><label>Sales:</label> {{ $formatCurrency(overrides.override_total) }}</div>
				<div><label>Items Sold:</label> {{ overrides.quantity_ordered }}</div>
			</div> -->
		</div>
	
	</div>

	<message-modal v-if="message" :message="message" :loading="loading" :errorStatus="messageStatus" :timeout="messageTimeout" @dismiss="message = ''" />

	<iframe id="download" src="" style="display: none;"></iframe>

	<help-popup :helpLink="{230:'Inventory Management Report', 231: 'Filtering Inventory Management Report'}" :tours="false"></help-popup>

</div>

</template>

<script>
import HelpPopup from '@/components/HelpPopup.vue'

export default {
	name: 'inventory-management',
    components: {
		'help-popup': HelpPopup
    },
	data() {
		return {
            loading: false,
			message: '',
			messageStatus: '',
			messageTimeout: 0,
			page: 1,
			showPagination: false,
			showFilters: true,
			filters: {},
            default_start_date: '',
            default_end_date: '',
			filterMessage: '',
			filterLoading: false,
			definition: [],
            filteredList: '',
			list: [],
			overrides: {},
			productList: [],
			paging: {},
			manufacturers: [],
			categories: [],
			showDetail: false,
			exportMessage: '',
			gettingExport: false,
			legacyLink: '',
			clientTypeCode: ''
		}
	},
	created() {
		this.getFilterLists()
		this.clearFilters()
	},
	methods: {
		clearFilters() {
			// if there are filters is session storgage?
			var sessionFilters = sessionStorage.getItem('inventory_management_report_filters')
			if (sessionFilters) {
				this.filters = JSON.parse(sessionFilters)
				sessionStorage.removeItem('inventory_management_report_filters')
			} else {
				this.filters = {
					page: 1,
					search_term: '',
					start_date: '',
					end_date: '',
					manufacturer_id: '',
					department_id: '',
					category_id: '',
					do_export: false
				}
				this.filteredList = ''

				var oneMonthAgo = new Date()
				oneMonthAgo.setMonth(oneMonthAgo.getMonth() -1)
				this.default_start_date = oneMonthAgo.toISOString().slice(0,10)

				this.default_end_date = new Date().toISOString().slice(0,10)
				this.filters.start_date = this.default_start_date
				this.filters.end_date = this.default_end_date
			}
			if (this.filters.manufacturer_id || this.filters.search_term) {
				this.getFilteredList()
			} else if (this.filters.department_id) {
				this.getDepartment(this.filters.department_id)
			} else {
				this.getList()
			}
		},
		getList() {
			if (this.loading) { return }
			this.loading = true
			if (this.filters.do_export) {
				this.message = 'Generating CSV for ' + this.list.length + ' items...<p><i>this could take a few moments</i></p>'
			} else {
				this.message = 'Generating report...<p><i>this could take a few moments</i></p>'
			}
			this.apiCall({ destination: 'get_report_inventory_management', data: this.filters }).then(obj => {
				this.loading = false
				this.messageStatus = obj.status
				if (this.filters.do_export) {
					this.filters.do_export = 0
					this.message = 'Downloading CSV...'
					// accommodate live server filecache latency
					setTimeout(() => {
						document.getElementById("download").src = obj.download_url
						this.gettingExport = false
						this.exportMessage = ''
						this.message = ''
					}, 500)
				} else {
					this.clientTypeCode = obj.client_type_code
					this.list = obj.list
					if (obj.overrides) {
						this.overrides = obj.overrides
					}
					if (obj.legacy_link) {
						this.legacyLink = obj.legacy_link
					}
					setTimeout(() => {
						this.message = ''
						document.getElementById('list_table').scroll({ top: 0, behavior: 'smooth' })
					}, 500)
				}
			}).catch(err => {
				console.log(err)
			})
		},
		getFilterLists() {
			this.apiCall({ destination: 'get_filter_lists_inventory_management', data: this.filters }).then(obj => {
				this.manufacturers = obj.manufacturers
			}).catch(err => {
				console.log(err)
			})
		},
		getDepartment(departmentId) {
			if (this.loading) { return }
			this.loading = true
			if (this.filters.do_export) {
				this.message = 'Generating CSV for ' + this.productList.length + ' items...<p><i>this could take a few moments</i></p>'
			} else {
				this.message = 'Generating report...<p><i>this could take a few moments</i></p>'
			}
			this.filters.department_id = departmentId
			this.apiCall({ destination: 'get_list_inventory_management_department', data: this.filters }).then(obj => {
				this.loading = false
				if (this.filters.do_export) {
					this.filters.do_export = 0
					// accommodate live server filecache latency
					this.message = 'Downloading CSV...'
					setTimeout(() => {
						document.getElementById("download").src = obj.download_url
						this.gettingExport = false
						this.message = ''
					}, 500)
				} else {
					this.productList = obj.list
					this.categories = obj.categories
					this.filteredList = obj.department
					setTimeout(() => {
						this.message = ''
						document.getElementById('list_wrapper').scroll({ top: 0, behavior: 'smooth' })
					}, 500)
				}
			}).catch(err => {
				console.log(err)
			})
		},
		getFilteredList() {
			if (this.loading) { return }
			this.loading = true
			if (this.filters.do_export) {
				this.message = 'Generating CSV for ' + this.list.length + ' items...<p><i>this could take a few moments</i></p>'
			} else {
				this.message = 'Generating report...<p><i>this could take a few moments</i></p>'
			}
			if (this.filters.manufacturer_id || this.filters.search_term) {
				this.filters.department_id = ''
			}
			this.apiCall({ destination: 'get_list_inventory_management_department', data: this.filters }).then(obj => {
				this.loading = false
				if (this.filters.do_export) {
					this.filters.do_export = 0
					// accommodate live server filecache latency
					this.message = 'Downloading CSV...'
					setTimeout(() => {
						document.getElementById("download").src = obj.download_url
						this.gettingExport = false
						this.message = ''
					}, 500)
				} else {
					this.productList = obj.list
					this.filteredList = obj.department
					setTimeout(() => {
						this.message = ''
						document.getElementById('list_wrapper').scroll({ top: 0, behavior: 'smooth' })
					}, 500)
				}
			}).catch(err => {
				console.log(err)
			})
		},
		goProduct(item) {
			if (!item.product_id) { return }
			// store filters in session storage
			sessionStorage.setItem('inventory_management_report_filters', JSON.stringify(this.filters))
			this.$router.push('/inventory/manage-products/detail/' + item.product_id + '?return=/reports/inventory-management')
		},
		backToDepartments() {
			this.filteredList = ''
			this.filters.category_id = ''
			this.filters.department_id = ''
			this.getList()
		},
		printWindow() {
			window.print()
		},
		doExport() {
			this.filters.do_export = true
			if (this.filters.manufacturer_id || this.filters.search_term) {
				this.getFilteredList()
			} else if (this.filters.department_id) {
				this.getDepartment(this.filters.department_id)
			} else {
				this.getList()
			}
		},
		getColumnTotal(column) {
			let total = 0
			if (this.filteredList) {
				this.productList.forEach((product) => {
					if (product[column]) {
						total += parseFloat(product[column])
					}
				})
			} else {
				this.list.forEach((department) => {
					if (department[column]) {
						total += parseFloat(department[column])
					}
				})
			}
			return total
		},
		getMargin(cost, price) {
			if (price > 0) {
				return (100 * ( 1 - ( cost / price) )).toFixed(1)
			} else {
				return 0
			}
		}
	}
}
</script>
